import { useRef } from 'react';
import { createPortal } from 'react-dom';
import type { CSSProperties, ReactNode } from 'react';
import { useLanguage } from './language-context';
import { useLayer } from './layer-stack';

// Sheet v2 (CB-17): a bottom sheet below 720 px, a centred dialog above (design
// modals M: `sheetup` / `sheetin` 240–320 ms, widths 320–960). Registers with the
// layer stack, so Esc / Back close only the top layer, page scroll is locked and
// focus returns to the opener. Portalled to <body>, so a sheet opened from inside
// another portalled overlay (the booking drawer's Cancel confirm) stacks above it
// instead of being trapped in the page's stacking context. A named single-purpose
// shell file.
//
// `headerless` drops the title bar so a caller can render its own header inside the
// body (the booking detail sheet shows the customer name inline with the status
// chip); the close control then floats top-right. `title` is still required — it
// labels the dialog for assistive tech either way. `width` is the desktop width.
export function Sheet({
  open,
  onClose,
  title,
  children,
  footer,
  headerless = false,
  width,
  role = 'dialog',
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  headerless?: boolean;
  width?: number;
  role?: 'dialog' | 'alertdialog';
}) {
  const { t } = useLanguage();
  const panelRef = useRef<HTMLDivElement>(null);
  useLayer(open, { kind: 'sheet', onDismiss: onClose, containerRef: panelRef });

  if (!open) return null;

  const style = width ? ({ '--sheet-w': `${width}px` } as CSSProperties) : undefined;
  return createPortal(
    <div className="modal-backdrop" onClick={onClose}>
      <div
        ref={panelRef}
        className="modal"
        role={role}
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        style={style}
        onClick={(e) => e.stopPropagation()}
      >
        <span className="modal__handle" aria-hidden="true" />
        {headerless ? (
          <button
            type="button"
            className="modal__close modal__close--float"
            aria-label={t('action.close')}
            onClick={onClose}
          >
            ✕
          </button>
        ) : (
          <div className="modal__head">
            <h2 className="modal__title">{title}</h2>
            <button
              type="button"
              className="modal__close"
              aria-label={t('action.close')}
              onClick={onClose}
            >
              ✕
            </button>
          </div>
        )}
        <div className="modal__body-wrap">{children}</div>
        {footer && <div className="modal__actions">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}
